'use client';

/**
 * Add Vendor Form Widget
 *
 * Two-panel collapsible form for vendor registration.
 * Panel 1: required fields (company, contact, category) with progress bar.
 * Panel 2: detailed fields (address, qualifications, terms) — collapsible.
 * Submits via BFF data gateway: api.getStrategyData('add_vendor', formData).
 *
 * @component_role Vendor Registration Form
 * @business_rule All 6 required fields must be filled before submission.
 *   Email must be valid format. ABN (if provided) must be 11 digits.
 * @user_outcome User registers a new vendor with company details,
 *   categories, qualifications, and terms.
 * @integration_point klyde_edge data gateway - add_vendor
 *
 * @layer presentation
 * @arch_component_type canvas_widget
 * @arch_domain vendor_portal
 */

import { useState, useCallback } from 'react';
import {
  Building2,
  User,
  Phone,
  Mail,
  ChevronUp,
  ChevronDown,
  CheckCircle,
  AlertCircle,
  Hash,
  Globe,
  MapPin,
  Plus,
  X,
  Loader2,
  ArrowLeft,
  Shield,
} from 'lucide-react';
import { api } from '@/lib/api/client';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import type { VPAddVendorResponse } from '@/lib/api/types';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils/cn';
import { toast } from 'sonner';

// ============================================================
// Mock Constants
// ============================================================

const PRIMARY_CATEGORIES = [
  'Furniture',
  'Lighting',
  'Floor Treatment',
  'Textiles',
  'Joinery',
  'Outdoor',
  'Art & Accessories',
  'Window Treatments',
  'Stone & Tile',
  'Other',
] as const;

const SECONDARY_CATEGORIES = [
  'Custom Joinery',
  'Bedroom',
  'Living Areas',
  'Bathroom',
  'Kitchen',
  'Dining',
  'Workspace',
  'Healthcare',
  'Education',
  'Hospitality',
  'Retail',
  'Aged Care',
  'Acoustic',
  'Sustainable',
] as const;

const PAYMENT_TERMS = [
  'Net 30',
  'Net 45',
  'Net 60',
  'EOM',
  'EOM + 30',
  'COD',
  'CIA',
  'Progress Payment',
  '50/50',
  'Custom',
] as const;

const QUALIFICATION_OPTIONS = [
  'AFRDI Blue Tick',
  'ISO 9001',
  'FSC Certified',
  'GECA Certified',
  'Good Environmental Choice',
  'PEFC Certified',
  'ISO 14001',
  'Greenguard',
  'Level Certified',
  'B Corp',
  'Carbon Neutral',
  'Indigenous Owned',
] as const;

const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'] as const;

const COUNTRIES = [
  'Australia',
  'New Zealand',
  'China',
  'United States',
  'United Kingdom',
  'Italy',
  'Germany',
  'Singapore',
  'Other',
] as const;

const LEAD_TIME_UNITS = ['days', 'weeks', 'months'] as const;

const CURRENCIES = ['AUD', 'USD', 'EUR', 'GBP', 'CNY'] as const;

// ============================================================
// Types
// ============================================================

export interface VendorFormData {
  // Required (Panel 1)
  company_name: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  primary_category: string;
  // Detailed (Panel 2)
  abn: string;
  street: string;
  suite: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  secondary_categories: string[];
  lead_time_value: string;
  lead_time_unit: string;
  moq_value: string;
  moq_currency: string;
  payment_terms: string;
  payment_terms_custom: string;
  qualifications: string[];
  custom_qualification: string;
  website: string;
  notes: string;
}

interface AddVendorFormWidgetProps {
  onBack?: () => void;
  onVendorCreated?: (data: VendorFormData) => void;
  entityId?: string;
  title?: string;
}

const INITIAL_FORM: VendorFormData = {
  company_name: '',
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  primary_category: '',
  abn: '',
  street: '',
  suite: '',
  suburb: '',
  state: '',
  postcode: '',
  country: 'Australia',
  secondary_categories: [],
  lead_time_value: '',
  lead_time_unit: 'weeks',
  moq_value: '',
  moq_currency: 'AUD',
  payment_terms: '',
  payment_terms_custom: '',
  qualifications: [],
  custom_qualification: '',
  website: '',
  notes: '',
};

// ============================================================
// Helpers
// ============================================================

const REQUIRED_FIELDS: (keyof VendorFormData)[] = [
  'company_name',
  'first_name',
  'last_name',
  'phone',
  'email',
  'primary_category',
];

function countCompleted(form: VendorFormData): number {
  return REQUIRED_FIELDS.filter((k) => {
    const v = form[k];
    return typeof v === 'string' && v.trim().length > 0;
  }).length;
}

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validateABN(abn: string): boolean {
  if (!abn) return true; // optional
  return /^\d{11}$/.test(abn.replace(/\s/g, ''));
}

// ============================================================
// Component
// ============================================================

export function AddVendorFormWidget({
  onBack,
  onVendorCreated,
}: AddVendorFormWidgetProps) {
  const createStrategy = useCapabilityStore((s) => s.strategyName('vendor_portal', 'create'));
  const [form, setForm] = useState<VendorFormData>({ ...INITIAL_FORM });
  const [detailedExpanded, setDetailedExpanded] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState('');
  const [submittedRef, setSubmittedRef] = useState<{ record_id?: number; reference?: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dedupResult, setVPAddVendorResponse] = useState<VPAddVendorResponse | null>(null);

  const completed = countCompleted(form);
  const progressPct = Math.round((completed / REQUIRED_FIELDS.length) * 100);
  const allRequired = completed === REQUIRED_FIELDS.length;

  // -- Field updaters --

  const set = useCallback(
    <K extends keyof VendorFormData>(key: K, value: VendorFormData[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      setErrors([]);
    },
    []
  );

  const toggleSecondaryCategory = useCallback((cat: string) => {
    setForm((prev) => {
      const has = prev.secondary_categories.includes(cat);
      if (has) {
        return { ...prev, secondary_categories: prev.secondary_categories.filter((c) => c !== cat) };
      }
      if (prev.secondary_categories.length >= 5) return prev;
      return { ...prev, secondary_categories: [...prev.secondary_categories, cat] };
    });
  }, []);

  const toggleQualification = useCallback((q: string) => {
    setForm((prev) => {
      const has = prev.qualifications.includes(q);
      if (has) {
        return { ...prev, qualifications: prev.qualifications.filter((x) => x !== q) };
      }
      return { ...prev, qualifications: [...prev.qualifications, q] };
    });
  }, []);

  const addCustomQualification = useCallback(() => {
    setForm((prev) => {
      const q = prev.custom_qualification.trim();
      if (!q || prev.qualifications.includes(q)) return prev;
      return {
        ...prev,
        qualifications: [...prev.qualifications, q],
        custom_qualification: '',
      };
    });
  }, []);

  // -- Validation --

  const validate = useCallback((): string[] => {
    const errs: string[] = [];
    if (!form.company_name.trim()) errs.push('Company name is required');
    if (!form.first_name.trim()) errs.push('First name is required');
    if (!form.last_name.trim()) errs.push('Last name is required');
    if (!form.phone.trim()) errs.push('Phone is required');
    if (!form.email.trim()) errs.push('Email is required');
    else if (!validateEmail(form.email)) errs.push('Invalid email format');
    if (!form.primary_category) errs.push('Primary category is required');
    if (!validateABN(form.abn)) errs.push('ABN must be 11 digits');
    return errs;
  }, [form]);

  // -- Submit --

  const handleSubmit = useCallback(async () => {
    const errs = validate();
    if (errs.length > 0) { setErrors(errs); return; }

    setIsSubmitting(true);
    setErrors([]);
    setVPAddVendorResponse(null);

    try {
      const res = await api.getStrategyData<any>(createStrategy!, {
        ...form,
        force_create: false,
      });

      if (!res.success) {
        setErrors([res.error || 'Failed to create vendor']);
        return;
      }

      // Dedup hit — show match info, let user decide
      if (res.data.dedup_hit) {
        setVPAddVendorResponse(res.data as VPAddVendorResponse);
        return;
      }

      // Success — vendor created
      setSubmittedName(form.company_name.trim());
      setSubmittedRef({ record_id: res.data.record_id, reference: res.data.reference });
      setSubmitted(true);
      onVendorCreated?.(form);
      toast.success(`Vendor "${form.company_name.trim()}" created`);
    } catch (err: any) {
      setErrors([err.message || 'Network error']);
    } finally {
      setIsSubmitting(false);
    }
  }, [form, validate, onVendorCreated]);

  const handleForceCreate = useCallback(async () => {
    setIsSubmitting(true);
    setVPAddVendorResponse(null);
    try {
      const res = await api.getStrategyData<any>(createStrategy!, {
        ...form,
        force_create: true,
      });
      if (!res.success) { setErrors([res.error || 'Failed to create vendor']); return; }
      setSubmittedName(form.company_name.trim());
      setSubmittedRef({ record_id: res.data.record_id, reference: res.data.reference });
      setSubmitted(true);
      onVendorCreated?.(form);
      toast.success(`Vendor "${form.company_name.trim()}" created`);
    } catch (err: any) {
      setErrors([err.message || 'Network error']);
    } finally {
      setIsSubmitting(false);
    }
  }, [form, onVendorCreated]);

  const handleReset = useCallback(() => {
    setForm({ ...INITIAL_FORM });
    setErrors([]);
    setSubmitted(false);
    setSubmittedName('');
    setSubmittedRef(null);
    setDetailedExpanded(false);
    setVPAddVendorResponse(null);
  }, []);

  // ============================================================
  // Success State
  // ============================================================

  if (submitted) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4 p-8">
        <div className="w-16 h-16 rounded-2xl bg-success/10 border border-success/20 flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-success" />
        </div>
        <h2 className="text-lg font-semibold">{submittedName} added!</h2>
        <p className="text-sm text-muted-foreground">Vendor has been registered in Klyde.</p>
        {submittedRef && (submittedRef.reference || submittedRef.record_id) && (
          <p className="text-xs text-muted-foreground">
            {submittedRef.reference && <span>{submittedRef.reference}</span>}
            {submittedRef.reference && submittedRef.record_id && <span> · </span>}
            {submittedRef.record_id && <span>Odoo ID: {submittedRef.record_id}</span>}
          </p>
        )}
        <div className="flex gap-3 mt-4">
          <Button variant="outline" size="sm" onClick={onBack}>
            Done
          </Button>
          <Button size="sm" onClick={handleReset}>
            <Plus className="w-4 h-4 mr-1.5" />
            Add Another Vendor
          </Button>
        </div>
      </div>
    );
  }

  // ============================================================
  // Form
  // ============================================================

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3 border-b border-border bg-background/95 backdrop-blur-sm flex-shrink-0">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Vendors</span>
        </button>
        <div className="h-4 w-px bg-border" />
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold">Add Vendor</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
            Required fields
          </span>
          <span className="text-[10px] text-muted-foreground font-medium">
            {completed}/{REQUIRED_FIELDS.length}
          </span>
          <div className="w-20 h-1 bg-muted rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500 ease-out',
                allRequired ? 'bg-success' : 'bg-primary'
              )}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Scrollable form body */}
      <div className="flex-1 overflow-y-auto scrollbar-thin p-4">
        <div className="max-w-2xl mx-auto space-y-6">
        {/* ── Panel 1: Required Fields ── */}
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 bg-card border-b border-border">
            <div className="w-5 h-5 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
              <span className="text-[9px] font-bold text-primary">1</span>
            </div>
            <div className="flex-1">
              <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider">Required</h3>
              <p className="text-[10px] text-muted-foreground mt-0.5">Minimum fields to register this vendor</p>
            </div>
            {allRequired && (
              <div className="flex items-center gap-1.5 text-[11px] text-success bg-success/10 border border-success/20 px-2.5 py-1 rounded-full">
                <CheckCircle className="w-3 h-3" />
                Complete
              </div>
            )}
          </div>
          <div className="p-5 space-y-4 bg-card">

          {/* Company Name */}
          <FieldRow icon={Building2} label="Company Name" required>
            <Input
              placeholder="Acme Furniture Pty Ltd"
              value={form.company_name}
              onChange={(e) => set('company_name', e.target.value)}
              className="h-9"
            />
          </FieldRow>

          {/* First / Last name */}
          <div className="grid grid-cols-2 gap-3">
            <FieldRow icon={User} label="First Name" required>
              <Input
                placeholder="Jane"
                value={form.first_name}
                onChange={(e) => set('first_name', e.target.value)}
                className="h-9"
              />
            </FieldRow>
            <FieldRow icon={User} label="Last Name" required>
              <Input
                placeholder="Smith"
                value={form.last_name}
                onChange={(e) => set('last_name', e.target.value)}
                className="h-9"
              />
            </FieldRow>
          </div>

          {/* Phone */}
          <FieldRow icon={Phone} label="Phone" required>
            <Input
              type="tel"
              placeholder="+61 4XX XXX XXX"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              className="h-9"
            />
          </FieldRow>

          {/* Email */}
          <FieldRow icon={Mail} label="Email" required>
            <Input
              type="email"
              placeholder="jane@acmefurniture.com.au"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              className="h-9"
            />
          </FieldRow>

          {/* Primary Category */}
          <FieldRow icon={Building2} label="Primary Category" required>
            <Select
              value={form.primary_category}
              onValueChange={(v) => set('primary_category', v)}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {PRIMARY_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldRow>
          </div>
        </div>

        {/* ── Panel 2: Detailed Fields (collapsible) ── */}
        <div className="rounded-xl border border-border overflow-hidden">
          <button
            type="button"
            onClick={() => setDetailedExpanded((p) => !p)}
            className="w-full flex items-center gap-3 px-4 py-3 bg-card border-b border-border hover:bg-muted transition-colors"
          >
            <div className="w-5 h-5 rounded-full bg-info/10 border border-info/20 flex items-center justify-center flex-shrink-0">
              <span className="text-[9px] font-bold text-info">2</span>
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider">Detailed</h3>
              <p className="text-[10px] text-muted-foreground mt-0.5">Enrichment fields — optional, can be added later</p>
            </div>
            <div className="flex items-center gap-2">
              {detailedExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </div>
          </button>

          {detailedExpanded && (
            <div className="p-5 space-y-5 bg-card">
              {/* ABN */}
              <FieldRow icon={Hash} label="ABN">
                <Input
                  placeholder="XX XXX XXX XXX"
                  value={form.abn}
                  onChange={(e) => set('abn', e.target.value)}
                  className="h-9"
                />
              </FieldRow>

              {/* Address */}
              <div className="space-y-3">
                <label className="text-xs font-bold text-foreground flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                  Address
                </label>
                <Input
                  placeholder="Street address"
                  value={form.street}
                  onChange={(e) => set('street', e.target.value)}
                  className="h-9"
                />
                <Input
                  placeholder="Suite / Unit (optional)"
                  value={form.suite}
                  onChange={(e) => set('suite', e.target.value)}
                  className="h-9"
                />
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    placeholder="Suburb"
                    value={form.suburb}
                    onChange={(e) => set('suburb', e.target.value)}
                    className="h-9"
                  />
                  <Select value={form.state} onValueChange={(v) => set('state', v)}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      {AU_STATES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Postcode"
                    value={form.postcode}
                    onChange={(e) => set('postcode', e.target.value)}
                    className="h-9"
                  />
                </div>
                <Select value={form.country} onValueChange={(v) => set('country', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Country" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t border-border" />

              {/* Secondary Categories — pill toggles */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-foreground">
                  Secondary Categories{' '}
                  <span className="text-muted-foreground font-normal">(max 5)</span>
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {SECONDARY_CATEGORIES.map((cat) => {
                    const active = form.secondary_categories.includes(cat);
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => toggleSecondaryCategory(cat)}
                        className={cn(
                          'text-[11px] px-2.5 py-1.5 rounded-md border transition-all',
                          active
                            ? 'bg-primary/10 border-primary/30 text-primary'
                            : 'bg-muted border-border text-muted-foreground hover:border-accent hover:text-foreground'
                        )}
                      >
                        {active && <span className="mr-1">✓</span>}
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="border-t border-border" />

              {/* Lead Time */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Lead Time</label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min={0}
                    placeholder="e.g. 6"
                    value={form.lead_time_value}
                    onChange={(e) => set('lead_time_value', e.target.value)}
                    className="h-9 w-24"
                  />
                  <Select
                    value={form.lead_time_unit}
                    onValueChange={(v) => set('lead_time_unit', v)}
                  >
                    <SelectTrigger className="h-9 w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_TIME_UNITS.map((u) => (
                        <SelectItem key={u} value={u}>
                          {u}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* MOQ */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">
                  Minimum Order Quantity
                </label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min={0}
                    placeholder="e.g. 5000"
                    value={form.moq_value}
                    onChange={(e) => set('moq_value', e.target.value)}
                    className="h-9 w-32"
                  />
                  <Select
                    value={form.moq_currency}
                    onValueChange={(v) => set('moq_currency', v)}
                  >
                    <SelectTrigger className="h-9 w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Payment Terms */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Payment Terms</label>
                <Select
                  value={form.payment_terms}
                  onValueChange={(v) => set('payment_terms', v)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select terms" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TERMS.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.payment_terms === 'Custom' && (
                  <Textarea
                    placeholder="Describe custom payment terms..."
                    value={form.payment_terms_custom}
                    onChange={(e) => set('payment_terms_custom', e.target.value)}
                    className="mt-1.5 min-h-[60px]"
                  />
                )}
              </div>

              <div className="border-t border-border" />

              {/* Qualifications — pill toggles + custom add */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-foreground">
                  Qualifications & Certifications
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {QUALIFICATION_OPTIONS.map((q) => {
                    const active = form.qualifications.includes(q);
                    return (
                      <button
                        key={q}
                        type="button"
                        onClick={() => toggleQualification(q)}
                        className={cn(
                          'text-[11px] px-2.5 py-1.5 rounded-md border transition-all',
                          active
                            ? 'bg-primary/10 border-primary/30 text-primary'
                            : 'bg-muted border-border text-muted-foreground hover:border-accent hover:text-foreground'
                        )}
                      >
                        {active && <span className="mr-1">✓</span>}
                        {q}
                      </button>
                    );
                  })}
                </div>
                {/* Custom qualifications already added */}
                {form.qualifications
                  .filter((q) => !QUALIFICATION_OPTIONS.includes(q as typeof QUALIFICATION_OPTIONS[number]))
                  .length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {form.qualifications
                      .filter((q) => !QUALIFICATION_OPTIONS.includes(q as typeof QUALIFICATION_OPTIONS[number]))
                      .map((q) => (
                        <span
                          key={q}
                          className="text-[11px] text-info bg-info/10 border border-info/20 px-2.5 py-1 rounded-md flex items-center gap-1.5"
                        >
                          <Shield className="w-3 h-3" />
                          {q}
                          <button
                            type="button"
                            onClick={() => toggleQualification(q)}
                            className="text-info/60 hover:text-info ml-0.5"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      ))}
                  </div>
                )}
                {/* Add custom */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Add custom certification..."
                    value={form.custom_qualification}
                    onChange={(e) => set('custom_qualification', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomQualification();
                      }
                    }}
                    className="h-8 text-xs"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 px-2"
                    onClick={addCustomQualification}
                    disabled={!form.custom_qualification.trim()}
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {/* Website */}
              <FieldRow icon={Globe} label="Website">
                <Input
                  type="url"
                  placeholder="https://www.acmefurniture.com.au"
                  value={form.website}
                  onChange={(e) => set('website', e.target.value)}
                  className="h-9"
                />
              </FieldRow>

              {/* Notes */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Internal Notes</label>
                <Textarea
                  placeholder="Additional information about this vendor..."
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  className="min-h-[80px]"
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Validation Errors ── */}
        {errors.length > 0 && (
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-4 h-4 text-destructive" />
              <span className="text-sm font-medium text-destructive">
                Please fix the following:
              </span>
            </div>
            <ul className="list-disc list-inside text-xs text-destructive space-y-0.5">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        {/* ── Dedup Warning ── */}
        {dedupResult && (
          <div className="rounded-lg border border-warning/20 bg-warning/5 p-4 space-y-3">
            <div className="flex items-center gap-2 text-warning font-medium">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">Possible duplicate found</span>
            </div>
            {dedupResult.company && (
              <p className="text-xs text-muted-foreground">
                Company: <strong className="text-foreground">{dedupResult.company.name}</strong>
                {' '}({dedupResult.company.match_reasons.join(', ')})
              </p>
            )}
            {dedupResult.contact && (
              <p className="text-xs text-muted-foreground">
                Contact: <strong className="text-foreground">{dedupResult.contact.name}</strong>
                {' '}({dedupResult.contact.match_reasons.join(', ')})
              </p>
            )}
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setVPAddVendorResponse(null)}>
                Edit Form
              </Button>
              <Button size="sm" variant="default" onClick={handleForceCreate}>
                Create Anyway
              </Button>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex items-center justify-between gap-3 pb-4">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-muted-foreground hover:text-foreground">
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating...</> : 'Add Vendor'}
          </Button>
        </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// FieldRow — label + icon layout for form fields
// ============================================================

function FieldRow({
  icon: Icon,
  label,
  required,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs font-bold text-foreground">
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
        {label}
        {required && <span className="text-destructive">*</span>}
      </label>
      {children}
    </div>
  );
}
